'use client';

import { FilterDialog } from '@/components/dialogs/filter-dialog/filter-dialog';
import { FilterIcon } from '@/components/icons/filter-icon';
import { LoadingSpinnerComponent } from '@/components/loading-spinner/loading-spinner';
import { Button } from '@/components/ui/button';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs/tabs';
import { useMoviesListFilters } from '@/hooks/movies/movies-list-filters';
import { useMoviesNearbyTheaters } from '@/hooks/movies/movies-nearby-theaters';
import { MovieCatalogCategories } from '@/types/movie-catalog';
import Link from 'next/link';
import { MoviesActiveFilters } from './movies-active-filters';
import { MoviesListGrid } from './movies-list-grid';
import { MoviesLocationControls } from './movies-location-controls';

const tabs = [
  { id: 'now-playing', label: 'Now Playing' },
  { id: 'coming-soon', label: 'Coming Soon' },
  { id: 'foreign', label: 'Foreign' },
  { id: 'events', label: 'Events' },
] as const;

export type MovieTab = typeof tabs[number]['id'];

type MoviesListViewProps = {
  activeTab?: MovieTab;
  popularGenreNames: string[];
};

export const MoviesListView = ({ activeTab = 'now-playing', popularGenreNames }: Readonly<MoviesListViewProps>): React.JSX.Element => {
  const {
    clearZipCode,
    handleTheaterResults,
    isLocationApplied,
    location,
    zipCode,
  } = useMoviesNearbyTheaters();
  const {
    activeFilters,
    filtersGrouped,
    handleFilterChange,
    handleSortChange,
    hasNoNearbyTheaters,
    isError: isMovieCatalogError,
    isLoading,
    movies,
    removeFilter,
    sortOptions,
  } = useMoviesListFilters({
    category: activeTab === 'coming-soon'
      ? MovieCatalogCategories.comingSoon
      : activeTab === 'foreign'
        ? MovieCatalogCategories.foreign
        : activeTab === 'events'
          ? MovieCatalogCategories.events
          : MovieCatalogCategories.nowPlaying,
    location,
    popularGenreNames,
  });

  return (
    <section className="container-xl flex flex-col gap-sm w-full"
      data-test="movies-list"
    >
      <div className="flex items-center justify-between">
        <h1 className="max-md:text-base"
          data-test="movies-near-title"
        >
          {isLocationApplied ? `Movies Near ${zipCode || 'You'}` : 'Browse Movies'}
        </h1>

        <div className="flex items-center gap-sm md:gap-md">
          <MoviesLocationControls isLoading={isLoading}
            onTheaterResults={handleTheaterResults}
          />

          {!isLoading &&
            <FilterDialog asAccordion
              withSeparator
              filters={filtersGrouped}
              groupOrder={['Rating', 'Genre']}
              radioOptions={sortOptions}
              radioOptionsTitle="Sort"
              showCloseButton={false}
              showTitle={false}
              onChange={handleFilterChange}
              onRadioChange={handleSortChange}
            >
              <Button aria-label="Filter Movies"
                data-test="filter-movies-trigger"
                variant="ghost"
              >
                <FilterIcon aria-hidden="true"
                  className="icon icon-primary"
                />
                <span className="font-bold text-sm max-md:hidden"
                  data-test="filter-movies"
                >
                  Filter Movies
                </span>
              </Button>
            </FilterDialog>
          }
        </div>
      </div>

      <MoviesActiveFilters activeFilters={activeFilters}
        zipCode={zipCode}
        onClearZipCode={clearZipCode}
        onRemoveFilter={removeFilter}
      />

      <Tabs className="min-w-0 w-full"
        data-test="movies-tabs"
        value={activeTab}
      >
        <ScrollArea className="min-w-0 w-full">
          <TabsList className="self-end">
            {tabs.map(tab =>
              <TabsTrigger key={tab.id}
                asChild
                value={tab.id}
              >
                <Link className="px-0 pb-xs text-base leading-6"
                  data-test={`tab-trigger-movies-${tab.id}`}
                  href={`/movies/${tab.id}`}
                >
                  {tab.label}
                </Link>
              </TabsTrigger>
            )}
          </TabsList>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </Tabs>

      {isLoading &&
        <div className="flex justify-center py-lg">
          <LoadingSpinnerComponent center="within" />
        </div>
      }
      {!isLoading && isMovieCatalogError &&
        <p data-test="movies-browse-error-state">We could not load movies right now. Please try again.</p>
      }
      {!isLoading && !isMovieCatalogError && hasNoNearbyTheaters &&
        <p data-test="movies-no-nearby-theaters-state">No theaters were found near this location.</p>
      }
      {!isLoading && !isMovieCatalogError && !hasNoNearbyTheaters && !!movies.length &&
        <MoviesListGrid movies={movies}
          tabLabel={tabs.find(tab => tab.id === activeTab)?.label || ''}
          zipCode={zipCode}
        />
      }
      {!isLoading && !isMovieCatalogError && !hasNoNearbyTheaters && !movies.length &&
        <p data-test="movies-empty-state">No movies were found</p>
      }

    </section>
  );
};
